import { payoutApi } from '../payout';

import {
  PaidProof,
  TrainingMe,
  TrainingMode,
  AssignOptions,
  TrainingCohort,
  TrainingPayReady,
  TrainingAssignment,
  CreateAssignmentRequest,
} from './types';

// TRAINING (owner 25 Oct 2026, F-20 S3): /api/v1/training. Injected into the
// payout API on purpose: approving a trainer Bonus with today's
// ApprovalPayoutDialog invalidates 'Payout', which refreshes these reads.
const TAG = 'Training';
const PAYOUT = 'Payout';

export const trainingApi = payoutApi
  .enhanceEndpoints({ addTagTypes: [TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      trainingMe: build.query<TrainingMe, void>({
        providesTags: [TAG],
        query: () => ({ method: 'get', url: 'training/me' }),
      }),
      assignOptions: build.query<AssignOptions, void>({
        providesTags: [TAG],
        query: () => ({ method: 'get', url: 'training/options' }),
      }),
      trainingCohort: build.query<TrainingCohort, void>({
        providesTags: [TAG],
        query: () => ({ method: 'get', url: 'training/cohort' }),
      }),
      // Training's Pay: the trainers' unpaid lines (Company Owner).
      trainingPayReady: build.query<TrainingPayReady, void>({
        providesTags: [TAG, PAYOUT],
        query: () => ({ method: 'get', url: 'training/pay-ready' }),
      }),
      trainingAssignment: build.query<TrainingAssignment, number>({
        providesTags: [TAG, PAYOUT],
        query: (id) => ({ method: 'get', url: `training/assignments/${id}` }),
      }),
      cancelAssignment: build.mutation<{ id: number }, number>({
        invalidatesTags: [TAG],
        query: (id) => ({
          method: 'post',
          url: `training/assignments/${id}/cancel`,
        }),
      }),
      trainingAssignments: build.query<
        { assignments: TrainingAssignment[] },
        void
      >({
        providesTags: [TAG, PAYOUT],
        query: () => ({ method: 'get', url: 'training/assignments' }),
      }),
      createAssignment: build.mutation<{ id: number }, CreateAssignmentRequest>(
        {
          invalidatesTags: [TAG],
          query: (data) => ({
            data,
            method: 'post',
            url: 'training/assignments',
          }),
        }
      ),
      setOfficialBaseline: build.mutation<
        { id: number },
        { id: number; note: string; cents: number }
      >({
        invalidatesTags: [TAG],
        query: ({ id, ...data }) => ({
          data,
          method: 'put',
          url: `training/assignments/${id}/baseline`,
        }),
      }),
      setPaidBefore: build.mutation<
        { id: number },
        {
          id: number;
          note: string;
          cents: number;
          proof?: PaidProof;
          mode: TrainingMode;
        }
      >({
        invalidatesTags: [TAG],
        query: ({ id, ...data }) => ({
          data,
          method: 'put',
          url: `training/assignments/${id}/paid-before`,
        }),
      }),
    }),
  });

export const {
  useTrainingMeQuery,
  useAssignOptionsQuery,
  useTrainingCohortQuery,
  useTrainingPayReadyQuery,
  useSetPaidBeforeMutation,
  useTrainingAssignmentQuery,
  useCancelAssignmentMutation,
  useCreateAssignmentMutation,
  useTrainingAssignmentsQuery,
  useSetOfficialBaselineMutation,
  useLazyTrainingAssignmentQuery,
} = trainingApi;
